import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ToggleGroup, ToggleGroupItem } from '@1trading/react';
import { useId } from 'react';
import { ContextTooltip } from '@/components/ui/context-tooltip';
import { FormSection } from '@/components/ui/form-section';
import { StatusChip } from '@/components/ui/status-chip';
import { formatUsd } from '@/utils/format';
import { isFiniteNumber } from '@/utils/number';
import { omit, stableStringify } from '@/utils/object';
import { BucketBands } from './bucket-bands';
import { scheduleEndBounds, scheduleStartBounds, todayUtc } from './date-bounds';
import { FIELD_DESCRIPTIONS } from './field-descriptions';
import { IndexSourcePicker } from './index-source-picker';
import { PROGRAM_TYPE_LABELS, SESSION_LABELS, setProgramType } from './mm-program-editing';
import { CheckboxField, DateField, NumberField } from './mm-program-fields';
import { isPerpetual, PROGRAM_TYPES, type ProgramInput, type ProgramType } from './mm-program-schema';
import { PayCurveChart, type PayTerms } from './program-charts';
import { ProgramRulesForm } from './program-rules-form';

type LeastPerformingInput = Extract<ProgramInput, { type: 'least_performing_bucket' }>;
type IndependentBucketInput = Extract<ProgramInput, { type: 'independent_buckets' }>['b1'];
type BucketName = 'b1' | 'b2';
const BUCKETS: BucketName[] = ['b1', 'b2'];

const patchBucket = (program: ProgramInput, bucket: BucketName, patch: { bps?: number; kpi?: number; nbbo?: boolean }): ProgramInput => {
  if (program.type === 'independent_buckets') {
    return bucket === 'b1' ? { ...program, b1: { ...program.b1, ...patch } } : { ...program, b2: { ...program.b2, ...patch } };
  }
  return bucket === 'b1' ? { ...program, b1: { ...program.b1, ...patch } } : { ...program, b2: { ...program.b2, ...patch } };
};

const patchIndependentBucket = (program: ProgramInput, bucket: BucketName, patch: Partial<IndependentBucketInput>): ProgramInput => {
  if (program.type !== 'independent_buckets') return program;
  return bucket === 'b1' ? { ...program, b1: { ...program.b1, ...patch } } : { ...program, b2: { ...program.b2, ...patch } };
};

const patchLeastPerforming = (program: ProgramInput, patch: Partial<Pick<LeastPerformingInput, 'threshold' | 'compensation' | 'exponent_start' | 'exponent'>>): ProgramInput =>
  program.type === 'least_performing_bucket' ? { ...program, ...patch } : program;

/** The pay terms of a bucket, or of the program when it pays on the worse bucket. */
const payTermsOf = (program: ProgramInput | undefined, bucket?: BucketName): PayTerms | undefined => {
  if (!program) return undefined;
  if (bucket) return program.type === 'independent_buckets' ? program[bucket] : undefined;
  return program.type === 'least_performing_bucket' ? program : undefined;
};

const PICK_SCHEDULE = (program: ProgramInput) => [program.start_date, program.end_date, program.type];
const PICK_BUCKETS = (program: ProgramInput) => [
  program.b1.bps,
  program.b1.kpi,
  program.b1.nbbo,
  program.b2.bps,
  program.b2.kpi,
  program.b2.nbbo,
  program.nbb_provider,
  program.nbb_instrument,
];
const PICK_PAY = (program: ProgramInput) =>
  program.type === 'independent_buckets'
    ? BUCKETS.map((bucket) => payTermsOf(program, bucket))
    : [program.threshold, program.compensation, program.exponent_start, program.exponent];
const PICK_OPEN_VOLUME = (program: ProgramInput) => program.ov ?? null;
const PICK_RULES = (program: ProgramInput) => program.rules ?? null;

const EditedChip = ({ children = 'Unsaved edits' }: { children?: string }) => (
  <StatusChip className="mt-1 px-2 py-0.5 text-xs" variant="info">
    {children}
  </StatusChip>
);
EditedChip.displayName = 'EditedChip';

const BucketHeading = ({ bucket }: { bucket: BucketName }) => <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{bucket.toUpperCase()}</p>;
BucketHeading.displayName = 'BucketHeading';

interface ProgramGroupFormProps {
  instrument: string;
  /** One schedule, or a C/E/W session set ordered C, E, W */
  programs: ProgramInput[];
  /** The same schedules as loaded, to draw edits against; absent for a schedule added since */
  saved?: ProgramInput[];
  onChange: (programs: ProgramInput[]) => void;
  /** Switches the schedule between one bucket set for all hours and a Core, Extended and Weekend set; absent hides the choice */
  onTradingHoursChange?: (sessioned: boolean) => void;
  /** First problem for `field` (e.g. `b1.bps`) on the program at `position` */
  problemAt: (position: number, field: string) => string | undefined;
}

export const ProgramGroupForm = ({ instrument, programs, saved, onChange, onTradingHoursChange, problemAt }: ProgramGroupFormProps) => {
  const variantId = useId();
  const shared = programs[0];
  const savedShared = saved?.[0];
  const isSessioned = shared.session !== undefined;
  const perpetual = isPerpetual(instrument);
  const nbboOn = shared.b1.nbbo === true || shared.b2.nbbo === true;

  const updateAll = (update: (program: ProgramInput) => ProgramInput) => onChange(programs.map(update));
  const updateOne = (position: number, update: (program: ProgramInput) => ProgramInput) =>
    onChange(programs.map((program, index) => (index === position ? update(program) : program)));
  const sharedProblem = (field: string) => programs.map((_, position) => problemAt(position, field)).find(Boolean);
  const editedStatus = (pick: (program: ProgramInput) => unknown) => (saved && stableStringify(programs.map(pick)) !== stableStringify(saved.map(pick)) ? <EditedChip /> : null);

  const setNbbo = (bucket: BucketName, enabled: boolean) =>
    updateAll((program) => {
      const next = patchBucket(program, bucket, { nbbo: enabled });
      return next.b1.nbbo === true || next.b2.nbbo === true ? next : omit(next, 'nbb_instrument', 'nbb_provider');
    });

  const bucketFields = (position: number, bucket: BucketName, isCompact: boolean) => {
    const program = programs[position];
    return (
      <>
        <NumberField
          description={FIELD_DESCRIPTIONS.bps}
          error={problemAt(position, `${bucket}.bps`)}
          label={isCompact ? `${bucket.toUpperCase()} half-width` : 'Half-width'}
          onChange={(bps) => updateOne(position, (item) => patchBucket(item, bucket, { bps }))}
          unit="bps"
          value={program[bucket].bps}
        />
        <NumberField
          description={FIELD_DESCRIPTIONS.kpi}
          error={problemAt(position, `${bucket}.kpi`)}
          label={isCompact ? `${bucket.toUpperCase()} quote size` : 'Quote size'}
          onChange={(kpi) => updateOne(position, (item) => patchBucket(item, bucket, { kpi }))}
          unit="USD"
          value={program[bucket].kpi}
        />
      </>
    );
  };

  const nbboFields = (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">NBBO</p>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {BUCKETS.map((bucket) => (
          <CheckboxField
            checked={shared[bucket].nbbo === true}
            description={FIELD_DESCRIPTIONS.nbbo}
            key={bucket}
            label={`${bucket.toUpperCase()} uses NBBO`}
            onChange={(enabled) => setNbbo(bucket, enabled)}
          />
        ))}
      </div>
      {nbboOn ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <IndexSourcePicker
            description={FIELD_DESCRIPTIONS.nbb_source}
            error={sharedProblem('nbb_provider') ?? sharedProblem('nbb_instrument')}
            instrument={instrument}
            label="NBBO source"
            onChange={({ provider, symbol }) => updateAll((program) => ({ ...program, nbb_provider: provider, nbb_instrument: symbol }))}
            provider={shared.nbb_provider}
            symbol={shared.nbb_instrument}
          />
        </div>
      ) : null}
    </div>
  );

  const hasCurve = shared.type === 'least_performing_bucket' && (shared.exponent_start != null || shared.exponent != null);
  const leastPerformingTerms = payTermsOf(shared);
  // Both bucket curves share a pay axis, so the larger compensation reads as the larger curve.
  const maxBucketCompensation = Math.max(0, ...BUCKETS.map((bucket) => payTermsOf(shared, bucket)?.compensation).filter(isFiniteNumber));

  return (
    <div className="flex flex-col">
      <FormSection status={saved ? editedStatus(PICK_SCHEDULE) : <EditedChip>New schedule</EditedChip>} title="Schedule">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <DateField
            description={FIELD_DESCRIPTIONS.start_date}
            error={sharedProblem('start_date')}
            label="Start (inclusive, UTC)"
            max={scheduleStartBounds(shared).max}
            onChange={(start_date) => updateAll((program) => ({ ...program, start_date }))}
            value={shared.start_date}
          />
          <DateField
            clearLabel="Clear: leave open"
            description={FIELD_DESCRIPTIONS.end_date}
            error={sharedProblem('end_date')}
            label="End (exclusive, UTC)"
            min={scheduleEndBounds(shared).min}
            onChange={(end_date) => updateAll((program) => ({ ...program, end_date }))}
            placeholder="Open, no end date"
            value={shared.end_date}
          />
          <div className="flex min-w-0 flex-col gap-1.5">
            <ContextTooltip description={FIELD_DESCRIPTIONS.type} htmlFor={variantId}>
              Variant
            </ContextTooltip>
            <Select onValueChange={(type) => updateAll((program) => setProgramType(program, type as ProgramType))} value={shared.type}>
              <SelectTrigger id={variantId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROGRAM_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {PROGRAM_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {onTradingHoursChange ? (
          <div className="flex flex-col items-start gap-1.5">
            <ContextTooltip description={FIELD_DESCRIPTIONS.trading_hours}>Trading hours</ContextTooltip>
            <ToggleGroup
              aria-label="Trading hours"
              className="w-fit"
              onValueChange={(value) => {
                if (value) onTradingHoursChange(value === 'sessions');
              }}
              size="sm"
              type="single"
              value={isSessioned ? 'sessions' : 'all'}
              variant="outline"
            >
              <ToggleGroupItem className="px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" value="all">
                All hours
              </ToggleGroupItem>
              <ToggleGroupItem className="px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" value="sessions">
                Core, Extended, Weekend
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        ) : null}
        {shared.start_date && shared.start_date.slice(0, 10) < todayUtc() ? (
          <p className="text-xs text-muted-foreground">Days before today are already scored. A change reaches them once those runs are rescored from Jobs.</p>
        ) : null}
      </FormSection>

      <FormSection status={editedStatus(PICK_BUCKETS)} title="Buckets">
        {isSessioned ? (
          <>
            <div className="grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-3">
              {programs.map((program, position) => (
                <div className="flex min-w-0 flex-col gap-3" key={program.session}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{SESSION_LABELS[program.session ?? ''] ?? program.session}</p>
                  {bucketFields(position, 'b1', true)}
                  {bucketFields(position, 'b2', true)}
                </div>
              ))}
            </div>
            {nbboFields}
            <BucketBands programs={programs} saved={saved} />
          </>
        ) : (
          <div className="grid items-start gap-8 xl:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-6">
              <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                {BUCKETS.map((bucket) => (
                  <div className="flex min-w-0 flex-col gap-3" key={bucket}>
                    <BucketHeading bucket={bucket} />
                    {bucketFields(0, bucket, false)}
                  </div>
                ))}
              </div>
              {nbboFields}
            </div>
            <BucketBands programs={programs} saved={saved} />
          </div>
        )}
      </FormSection>

      {shared.type === 'least_performing_bucket' && leastPerformingTerms ? (
        <FormSection status={editedStatus(PICK_PAY)} title="Pay">
          <div className="grid items-start gap-8 xl:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField
                  description={FIELD_DESCRIPTIONS.threshold}
                  error={sharedProblem('threshold')}
                  label="Threshold"
                  onChange={(threshold) => updateAll((program) => patchLeastPerforming(program, { threshold }))}
                  unit="%"
                  value={shared.threshold}
                />
                <NumberField
                  description={FIELD_DESCRIPTIONS.compensation}
                  error={sharedProblem('compensation')}
                  label="Compensation"
                  onChange={(compensation) => updateAll((program) => patchLeastPerforming(program, { compensation }))}
                  unit="USD / month"
                  value={shared.compensation}
                />
              </div>
              <CheckboxField
                checked={hasCurve}
                description={FIELD_DESCRIPTIONS.pay_curve}
                label="Pay on an exponent curve below the threshold"
                onChange={(enabled) =>
                  updateAll((program) =>
                    enabled
                      ? patchLeastPerforming(program, { exponent_start: shared.exponent_start ?? 0, exponent: shared.exponent ?? 7 })
                      : omit(program, 'exponent_start', 'exponent'),
                  )
                }
              />
              {hasCurve ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <NumberField
                    description={FIELD_DESCRIPTIONS.exponent_start}
                    error={sharedProblem('exponent_start')}
                    label="Exponent start"
                    onChange={(exponent_start) => updateAll((program) => patchLeastPerforming(program, { exponent_start }))}
                    unit="%"
                    value={shared.exponent_start}
                  />
                  <NumberField
                    description={FIELD_DESCRIPTIONS.exponent}
                    error={sharedProblem('exponent')}
                    label="Exponent"
                    onChange={(exponent) => updateAll((program) => patchLeastPerforming(program, { exponent }))}
                    value={shared.exponent}
                  />
                </div>
              ) : null}
            </div>
            <PayCurveChart draft={leastPerformingTerms} saved={payTermsOf(savedShared)} title="Pay on the worse bucket" />
          </div>
        </FormSection>
      ) : (
        <FormSection status={editedStatus(PICK_PAY)} title="Pay">
          {BUCKETS.map((bucket) => {
            const pay = payTermsOf(shared, bucket);
            const setPay = (patch: Partial<IndependentBucketInput>) => updateAll((program) => patchIndependentBucket(program, bucket, patch));
            return (
              <div className="grid items-start gap-8 xl:grid-cols-2" key={bucket}>
                <div className="grid content-start grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <BucketHeading bucket={bucket} />
                  </div>
                  <NumberField
                    description={FIELD_DESCRIPTIONS.threshold}
                    error={sharedProblem(`${bucket}.threshold`)}
                    label="Threshold"
                    onChange={(threshold) => setPay({ threshold })}
                    unit="%"
                    value={pay?.threshold}
                  />
                  <NumberField
                    description={FIELD_DESCRIPTIONS.compensation}
                    error={sharedProblem(`${bucket}.compensation`)}
                    label="Compensation"
                    onChange={(compensation) => setPay({ compensation })}
                    unit="USD / month"
                    value={pay?.compensation}
                  />
                  <NumberField
                    description={FIELD_DESCRIPTIONS.exponent_start}
                    error={sharedProblem(`${bucket}.exponent_start`)}
                    label="Exponent start"
                    onChange={(exponent_start) => setPay({ exponent_start: exponent_start ?? null })}
                    unit="%"
                    value={pay?.exponent_start}
                  />
                  <NumberField
                    description={FIELD_DESCRIPTIONS.exponent}
                    error={sharedProblem(`${bucket}.exponent`)}
                    label="Exponent"
                    onChange={(exponent) => setPay({ exponent: exponent ?? null })}
                    value={pay?.exponent}
                  />
                </div>
                {pay ? <PayCurveChart draft={pay} maxCompensation={maxBucketCompensation} saved={payTermsOf(savedShared, bucket)} title={`${bucket.toUpperCase()} pay`} /> : null}
              </div>
            );
          })}
        </FormSection>
      )}

      {perpetual ? (
        <>
          <FormSection status={editedStatus(PICK_OPEN_VOLUME)} title="Open volume credit">
            <CheckboxField
              checked={shared.ov !== undefined}
              description={FIELD_DESCRIPTIONS.ov}
              label="Credit open volume"
              onChange={(enabled) => updateAll((program) => (enabled ? { ...program, ov: shared.ov ?? { b2_multiple: 0, compensation_fraction: 0 } } : omit(program, 'ov')))}
            />
            {shared.ov ? (
              <div className="grid items-start gap-8 xl:grid-cols-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <NumberField
                    description={FIELD_DESCRIPTIONS['ov.b2_multiple']}
                    error={sharedProblem('ov.b2_multiple')}
                    label="B2 size multiple"
                    onChange={(b2_multiple) =>
                      updateAll((program) => ({ ...program, ov: { compensation_fraction: 0, ...program.ov, b2_multiple } as NonNullable<ProgramInput['ov']> }))
                    }
                    unit="×"
                    value={shared.ov.b2_multiple}
                  />
                  <NumberField
                    description={FIELD_DESCRIPTIONS['ov.compensation_fraction']}
                    error={sharedProblem('ov.compensation_fraction')}
                    label="Credit"
                    onChange={(compensation_fraction) =>
                      updateAll((program) => ({ ...program, ov: { b2_multiple: 0, ...program.ov, compensation_fraction } as NonNullable<ProgramInput['ov']> }))
                    }
                    scale={100}
                    unit="% of max pay"
                    value={shared.ov.compensation_fraction}
                  />
                </div>
                <OpenVolumeTargets programs={programs} />
              </div>
            ) : null}
          </FormSection>
          <ProgramRulesForm instrument={instrument} saved={savedShared} shared={shared} sharedProblem={sharedProblem} status={editedStatus(PICK_RULES)} updateAll={updateAll} />
        </>
      ) : null}
    </div>
  );
};
ProgramGroupForm.displayName = 'ProgramGroupForm';

/** The open volume each session must hold for its time to count: the multiple times that session's B2 quote size. */
const OpenVolumeTargets = ({ programs }: { programs: ProgramInput[] }) => (
  <dl className="flex flex-col gap-3 rounded-lg bg-muted/40 p-4 text-sm">
    {programs.map((program) => {
      const multiple = program.ov?.b2_multiple;
      const target = isFiniteNumber(multiple) && isFiniteNumber(program.b2.kpi) ? multiple * program.b2.kpi : undefined;
      return (
        <div className="flex flex-col gap-0.5" key={program.session ?? 'all'}>
          <dt className="text-xs text-muted-foreground">Open volume target{program.session ? `, ${SESSION_LABELS[program.session] ?? program.session}` : ''}</dt>
          <dd className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-lg font-semibold">{target === undefined ? '—' : formatUsd(target, 0)}</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {multiple ?? '—'} × B2 quote size {isFiniteNumber(program.b2.kpi) ? formatUsd(program.b2.kpi, 0) : '—'}
            </span>
          </dd>
        </div>
      );
    })}
  </dl>
);
OpenVolumeTargets.displayName = 'OpenVolumeTargets';
